import React, { useEffect, useMemo, useRef } from 'react';
import { useSelector } from 'react-redux';
import { useNavigate } from 'react-router-dom';
import { MapContainer, Marker, TileLayer, useMap } from 'react-leaflet';
import L from 'leaflet';
import 'leaflet/dist/leaflet.css';
import '../styles/requests.scss';
import { useRequests } from '../hooks/useRequests';
import { FiRefreshCw } from 'react-icons/fi';

const destinationPinIcon = new L.Icon({
    iconUrl: 'https://unpkg.com/leaflet@1.9.4/dist/images/marker-icon.png',
    iconRetinaUrl: 'https://unpkg.com/leaflet@1.9.4/dist/images/marker-icon-2x.png',
    shadowUrl: 'https://unpkg.com/leaflet@1.9.4/dist/images/marker-shadow.png',
    iconSize: [25, 41],
    iconAnchor: [12, 41],
    popupAnchor: [1, -34],
    shadowSize: [41, 41],
});

const makeStatusIcon = (className) =>
    L.divIcon({
        className,
        iconSize: [16, 16],
        iconAnchor: [8, 8],
    });

const statusToClass = {
    open: 'req-open',
    accepted: 'req-accepted',
    cancelled: 'req-cancelled',
    completed: 'req-completed',
    expired: 'req-expired',
};

const Recenter = ({ center }) => {
    const map = useMap();
    useEffect(() => {
        map.setView(center);
    }, [center, map]);
    return null;
};

const formatStatus = (status) => {
    switch (status) {
        case 'open':
            return 'قيد الانتظار';
        case 'accepted':
            return 'مقبول';
        case 'completed':
            return 'مكتمل';
        case 'cancelled':
            return 'ملغي';
        case 'expired':
            return 'منتهي';
        default:
            return status || '';
    }
};

const formatOfferStatus = (status) => {
    switch (status) {
        case 'pending': return 'قيد الانتظار';
        case 'accepted': return 'مقبول';
        case 'rejected': return 'مرفوض';
        case 'expired': return 'منتهي';
        case 'cancelled': return 'ملغي';
        default: return status || '';
    }
};

const formatDate = (value) => {
    if (!value) return '';
    const date = new Date(value);
    return date.toLocaleString('ar-LY');
};

export const Requests = () => {
    const user = useSelector((state) => state.userController.user);
    const navigate = useNavigate();
    const mapRef = useRef(null);

    const {
        requests,
        loading,
        selectedRequest,
        selectedRequestId,
        setSelectedRequestId,
        from,
        to,
        setFrom,
        setTo,
        fetchRequests,
        offers,
        offersLoading,
    } = useRequests();

    const toggleSelectedRequest = (requestId) => {
        if (!requestId) return;
        setSelectedRequestId((prev) => (String(prev) === String(requestId) ? null : requestId));
    };

    const visibleRequests = useMemo(() => {
        if (!selectedRequestId) return requests;
        return requests.filter((request) => String(request._id) === String(selectedRequestId));
    }, [requests, selectedRequestId]);

    useEffect(() => {
        if (!user) navigate('/login');
    }, [user, navigate]);

    const center = useMemo(() => {
        const points = visibleRequests
            .filter((r) => r?.startPoint?.latitude && r?.startPoint?.longitude)
            .map((r) => ({ lat: r.startPoint.latitude, lng: r.startPoint.longitude }));
        if (!points.length) return [32.8872, 13.1913];
        const avgLat = points.reduce((sum, p) => sum + p.lat, 0) / points.length;
        const avgLng = points.reduce((sum, p) => sum + p.lng, 0) / points.length;
        return [avgLat, avgLng];
    }, [visibleRequests]);

    const statusIcons = useMemo(() => {
        const map = {};
        Object.entries(statusToClass).forEach(([status, cls]) => {
            map[status] = makeStatusIcon(`req-dot ${cls}`);
        });
        return map;
    }, []);

    const getCancelReason = (request) => {
        if (!request) return '';
        return (
            request.cancelReason ||
            request.cancellationReason ||
            request.reason ||
            request.endReason ||
            ''
        );
    };

    return (
        <section className="container requests-page">
            <header className="requests-header">
                <div>
                    <h2>الطلبات</h2>
                    <p className="muted">اختر الفترة لعرض جميع الطلبات</p>
                </div>
                <div className="filters">
                    <label>
                        من
                        <input
                            type="date"
                            value={from}
                            onChange={(e) => setFrom(e.target.value)}
                        />
                    </label>
                    <label>
                        إلى
                        <input
                            type="date"
                            value={to}
                            onChange={(e) => setTo(e.target.value)}
                        />
                    </label>
                    <button type="button" onClick={fetchRequests} disabled={loading}>
                        {loading ? 'جار التحميل...' : 'تحديث'}
                    </button>
                </div>
            </header>

            <div className="map-and-detail">
                <div className="map-card">
                    <MapContainer
                        center={center}
                        zoom={7}
                        minZoom={3}
                        maxZoom={18}
                        maxBounds={[[18, 7], [37, 28]]}
                        maxBoundsViscosity={0.5}
                        scrollWheelZoom
                        whenCreated={(instance) => {
                            mapRef.current = instance;
                            setTimeout(() => instance.invalidateSize(), 120);
                        }}
                    >
                        <TileLayer
                            url="https://{s}.google.com/vt/lyrs=m&x={x}&y={y}&z={z}"
                            subdomains={['mt0', 'mt1', 'mt2', 'mt3']}
                            attribution="Map data © Google"
                        />
                        <Recenter center={center} />

                        {visibleRequests
                            .filter((req) => req?.startPoint?.latitude && req?.startPoint?.longitude)
                            .map((request) => {
                                const icon = statusIcons[request.status] || statusIcons.open;
                                return (
                                    <Marker
                                        key={request._id}
                                        position={[request.startPoint.latitude, request.startPoint.longitude]}
                                        icon={icon}
                                        eventHandlers={{
                                            click: () => toggleSelectedRequest(request._id),
                                        }}
                                    />
                                );
                            })}

                        {selectedRequest?.endingPoint?.latitude && selectedRequest?.endingPoint?.longitude ? (
                            <Marker
                                position={[selectedRequest.endingPoint.latitude, selectedRequest.endingPoint.longitude]}
                                icon={destinationPinIcon}
                            />
                        ) : null}
                    </MapContainer>
                </div>

                <div className="detail-card">
                    {selectedRequest ? (
                        <>
                            <div className="detail-header">
                                <div>
                                    <h3>{selectedRequest.userName}</h3>
                                    <p className="muted">{formatDate(selectedRequest.createdAt)}</p>
                                </div>
                                <span className={`status-badge ${selectedRequest.status || ''}`}>
                                    {formatStatus(selectedRequest.status)}
                                </span>
                            </div>
                            <div className="detail-grid">
                                <div className="item">
                                    <span className="label">الهاتف</span>
                                    <span className="value">{selectedRequest.userPhoneNumber}</span>
                                </div>
                                <div className="item">
                                    <span className="label">نوع المشكلة</span>
                                    <span className="value">{selectedRequest.problemType}</span>
                                </div>
                                <div className="item">
                                    <span className="label">نوع السيارة</span>
                                    <span className="value">{selectedRequest.carType}</span>
                                </div>
                                <div className="item">
                                    <span className="label">السعر</span>
                                    <span className="value">{selectedRequest.userPrice || selectedRequest.basePrice || 0} د.ل</span>
                                </div>
                                <div className="item">
                                    <span className="label">البداية</span>
                                    <span className="value">{selectedRequest.startPoint?.address}</span>
                                </div>
                                <div className="item">
                                    <span className="label">النهاية</span>
                                    <span className="value">{selectedRequest.endingPoint?.address}</span>
                                </div>
                                {selectedRequest.status === 'cancelled' ? (
                                    <div className="item">
                                        <span className="label">سبب الإلغاء</span>
                                        <span className="value">{getCancelReason(selectedRequest) || 'غير متوفر'}</span>
                                    </div>
                                ) : null}
                            </div>

                            <div className="offers-section">
                                <h4 className="offers-title">
                                    العروض
                                    {!offersLoading && (
                                        <span className="offers-count">{offers.length}</span>
                                    )}
                                </h4>
                                {offersLoading ? (
                                    <div className="offers-loading">جار التحميل...</div>
                                ) : offers.length === 0 ? (
                                    <div className="placeholder">لا توجد عروض لهذا الطلب</div>
                                ) : (
                                    <div className="offers-list">
                                        {offers.map((offer) => (
                                            <div key={offer._id} className={`offer-row offer-row--${offer.status}`}>
                                                <div className="offer-row__top">
                                                    <div className="offer-row__driver">
                                                        <span className="offer-row__name">{offer.driverName}</span>
                                                        <span className="offer-row__phone">{offer.driverPhoneNumber}</span>
                                                    </div>
                                                    <span className={`status-badge ${offer.status}`}>
                                                        {formatOfferStatus(offer.status)}
                                                    </span>
                                                </div>
                                                <div className="offer-row__meta">
                                                    <span className="offer-row__price">{offer.price} د.ل</span>
                                                    {offer.driverDistanceKm != null && (
                                                        <span>{Number(offer.driverDistanceKm).toFixed(1)} كم</span>
                                                    )}
                                                    {offer.driverEtaMinutes != null && (
                                                        <span>{offer.driverEtaMinutes} دقيقة</span>
                                                    )}
                                                    <span className="offer-row__date">{formatDate(offer.createdAt)}</span>
                                                </div>
                                            </div>
                                        ))}
                                    </div>
                                )}
                            </div>
                        </>
                    ) : (
                        <div className="placeholder">اختر طلباً من الخريطة أو القائمة لرؤية التفاصيل</div>
                    )}
                </div>
            </div>

            <div className="requests-list">
                <div className="requests-list__header">
                    <div>
                        <h4>الطلبات</h4>
                        <p className="muted">{requests.length} طلب</p>
                    </div>
                    <button className="refresh" onClick={fetchRequests} disabled={loading}>
                        <FiRefreshCw />
                        <span>{loading ? 'جار التحديث...' : 'تحديث'}</span>
                    </button>
                </div>
                {requests.length ? (
                    <div className="list">
                        {requests.map((req) => (
                            <button
                                key={req._id}
                                className={`row ${selectedRequestId === req._id ? 'active' : ''}`}
                                onClick={() => toggleSelectedRequest(req._id)}
                            >
                                <div className="row-main">
                                    <div>
                                        <p className="name">{req.userName}</p>
                                        <p className="phone">{req.userPhoneNumber}</p>
                                    </div>
                                    <span className={`status-badge ${req.status || ''}`}>{formatStatus(req.status)}</span>
                                </div>
                                <div className="row-meta">
                                    <span>{req.problemType}</span>
                                    <span>{req.carType}</span>
                                    <span>{formatDate(req.createdAt)}</span>
                                    {req.status === 'cancelled' ? <span>{getCancelReason(req) || 'بدون سبب'}</span> : null}
                                </div>
                            </button>
                        ))}
                    </div>
                ) : (
                    <div className="placeholder">لا توجد طلبات في هذه الفترة</div>
                )}
            </div>
        </section>
    );
};
